import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { api, unwrap } from "@/shared/lib/apiClient";
import {
  overviewKeys,
  patchKeys,
  raidContentKeys,
  setupKeys,
} from "@/shared/lib/queryKeys";
import type { PatchFormValues } from "../settings.patch.schemas";

function invalidatePatchDependents(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: patchKeys.all }),
    queryClient.invalidateQueries({ queryKey: raidContentKeys.all }),
    queryClient.invalidateQueries({ queryKey: overviewKeys.all }),
    queryClient.invalidateQueries({ queryKey: setupKeys.all }),
  ]);
}

export function useCreatePatch() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (body: PatchFormValues) =>
      unwrap(await api.POST("/api/patches", { body })),
    onSuccess: () => invalidatePatchDependents(queryClient),
    onError: (error) =>
      showApiError(error, { title: "Kunde inte skapa patchen" }),
  });
}

export function useUpdatePatch() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PatchFormValues }) =>
      unwrap(
        await api.PUT("/api/patches/{id}", {
          params: { path: { id } },
          body,
        }),
      ),
    onSuccess: () => invalidatePatchDependents(queryClient),
    onError: (error) =>
      showApiError(error, { title: "Kunde inte spara patchen" }),
  });
}

export function useDeletePatch() {
  const queryClient = useQueryClient();
  const { showApiError } = useErrorDialog();
  return useMutation({
    mutationFn: async (id: string) => {
      unwrap(
        await api.DELETE("/api/patches/{id}", {
          params: { path: { id } },
        }),
      );
    },
    onSuccess: () => invalidatePatchDependents(queryClient),
    onError: (error) =>
      showApiError(error, { title: "Kunde inte ta bort patchen" }),
  });
}
